
import CheckCardGroup from './CheckCardGroup.vue'
import TypedDateInput from '~/components/TypedDateInput.vue'

export function fmtPhone (v) {
    const d = String(v || '').replace(/\D/g, '').slice(0, 10)
    if (!d.length) { return '' }
    if (d.length < 4) { return `(${d}` }
    if (d.length < 7) { return `(${d.slice(0, 3)}) ${d.slice(3)}` }
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

export default {
    components: { CheckCardGroup, TypedDateInput },
    props: {
        label: { type: String, required: true },
        value: { type: String, default: '—' },
        sub: { type: String, default: null },
        type: { type: String, default: 'text' },
        options: { type: Array, default: () => [] },
        editValue: { type: [String, Number, Boolean, Object], default: null },
        min: { type: String, default: null },
        max: { type: String, default: null },
    },
    data () {
        // draftMonth/draftYear only matter for type 'coverage' (the EXACT
        // month/year selects) — harmless unused data for every other type.
        // draftIncomplete: a half-typed date ('date' type), which never
        // reaches `draft`, so Save has to be told.
        return { editing: false, draft: null, flash: false, draftMonth: '', draftYear: '', draftIncomplete: false }
    },
    computed: {
        monthNames () { return this.$store.getters.months },
        // Current year + 9 future years — same range MedicareSituation.vue
        // offers (components/schedule/v2/steps/MedicareSituation.vue:115-119).
        yearOptions () {
            const y = new Date().getFullYear()
            return Array.from({ length: 10 }, (_, i) => y + i)
        },
    },
    watch: {
        // Mirrors MedicareSituation.vue's syncEffectiveDate watch (only
        // month/year changes write effective_date — picking a non-EXACT
        // urgency does NOT clear it, matching the scheduler's behavior).
        draftMonth () { this.syncCoverageDate() },
        draftYear () { this.syncCoverageDate() },
    },
    methods: {
        fmtPhone,
        startEdit () {
            if (this.type === 'name') {
                this.draft = { ...(this.editValue || { first: '', middle: '', last: '' }) }
            } else if (this.type === 'coverage') {
                this.draft = { ...(this.editValue || { coverage_urgency: null, effective_date: null }) }
                // Prefill month/year from any existing effective_date (even a
                // legacy one saved before coverage_urgency existed) so the
                // EXACT selects don't come up blank if the user picks EXACT —
                // same prefill MedicareSituation.vue does unconditionally.
                // Parse the 'YYYY-MM-DD' string directly rather than via
                // `new Date(...)` — that parses date-only ISO as UTC
                // midnight, but .getMonth()/.getFullYear() read it back in
                // LOCAL time, so in any behind-UTC (US) timezone the date
                // reads back as the previous day (a wrong, earlier month for
                // 1st-of-month dates). Splitting the string sidesteps any
                // timezone conversion entirely.
                const [y, m] = (this.draft.effective_date || '').split('-')
                this.draftMonth = m ? Number(m) : ''
                this.draftYear = y ? Number(y) : ''
            } else {
                this.draft = this.editValue ?? ''
            }
            this.editing = true
            this.flash = false
            this.draftIncomplete = false
        },
        onUrgency (v) {
            // Just writes the enum value, same as the scheduler's
            // `@click.prevent='target.coverage_urgency = c.value'` — no
            // side effect on effective_date when switching away from EXACT.
            this.draft.coverage_urgency = v
        },
        syncCoverageDate () {
            if (this.type !== 'coverage') { return }
            if (this.draftMonth && this.draftYear) {
                this.draft.effective_date = `${this.draftYear}-${String(this.draftMonth).padStart(2, '0')}-01`
            } else {
                this.draft.effective_date = null
            }
        },
        invalid () {
            if (this.type === 'phone') { return String(this.draft).replace(/\D/g, '').length !== 10 }
            if (this.type === 'zip') { return !/^\d{5}$/.test(this.draft) }
            if (this.type === 'date') { return !this.draft || this.draftIncomplete }
            if (this.type === 'name') { return !this.draft.first?.trim() || !this.draft.last?.trim() }
            if (this.type === 'coverage') {
                if (!this.draft.coverage_urgency) { return true }
                if (this.draft.coverage_urgency === 'EXACT' && !this.draft.effective_date) { return true }
                return false
            }
            // Only an actual option may be saved. Identity against the option
            // list rather than a falsiness check, because `false` is a real
            // answer on the yes/no rows (collecting_ssi, hsa) while an unset
            // row starts as '' (startEdit coerces nil for the text inputs).
            if (this.type === 'options') { return !this.options.some(o => o.value === this.draft) }
            return false
        },
        save () {
            if (this.invalid()) {
                this.flash = false
                this.$nextTick(() => { this.flash = true })
                return
            }
            if (this.type === 'name') {
                this.$emit('save', {
                    first: this.draft.first.trim(),
                    middle: this.draft.middle.trim(),
                    last: this.draft.last.trim(),
                })
            } else {
                this.$emit('save', this.draft)
            }
            this.editing = false
        },
    },
}
